import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * GoalCelebration — Iter 243 redesign.
 *
 * Minimal cinematic goal celebration centred on the Sora 2 broadcast-
 * quality MP4.  The previous iteration layered a 3D isometric pitch,
 * goal-net, lens flare and dashed ball trajectory behind a small
 * rolling PNG — now that the Sora 2 video IS the celebration, all of
 * that legacy scaffolding is removed.  The surviving elements are:
 *   • Radial flash backdrop
 *   • Sora 2 video, centred (60vw × 34vw, max 520×290)
 *   • Slide-in GOAL banner (team colour)
 *   • Cinematic GOOOAL takeover text + scorer
 *   • Score / minute badge
 *   • Confetti burst
 *
 * Public API (back-compatible with iter 145+):
 *   <GoalCelebration
 *     trigger="home-1717"
 *     side="home" | "away"
 *     scorer="Player Name"
 *     soundEnabled
 *     onDone={() => ...}
 *     teamName="Manningham United"
 *     teamColor="#2563eb"
 *     minute={67}
 *     score={{ home: 3, away: 4 }}
 *   />
 */

const GOAL_SOUND_SRC = "/sounds/goal-cheer.mp3";
let goalAudioEl = null;

const getGoalAudio = () => {
  if (typeof window === "undefined") return null;
  if (!goalAudioEl) {
    goalAudioEl = new Audio(GOAL_SOUND_SRC);
    goalAudioEl.preload = "auto";
    goalAudioEl.volume = 0.85;
  }
  return goalAudioEl;
};

if (typeof window !== "undefined" && !window.__gcAudioArmed) {
  window.__gcAudioArmed = true;
  const arm = () => {
    const a = getGoalAudio();
    if (!a) return;
    const prevVol = a.volume;
    a.volume = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => { a.pause(); a.currentTime = 0; a.volume = prevVol; })
       .catch(() => { a.volume = prevVol; });
    } else {
      a.volume = prevVol;
    }
    window.removeEventListener("pointerdown", arm);
    window.removeEventListener("keydown", arm);
    window.removeEventListener("touchstart", arm);
  };
  window.addEventListener("pointerdown", arm, { once: true });
  window.addEventListener("keydown", arm, { once: true });
  window.addEventListener("touchstart", arm, { once: true });
}

const playGoalSound = () => {
  try {
    const a = getGoalAudio();
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch { /* silent */ }
};

// Total sequence runtime ~4.6s — matches Sora 2 MP4 length.
const SEQUENCE_MS = 4600;

export const GoalCelebration = ({
  trigger,
  side = "home",
  scorer = "",
  onDone,
  soundEnabled = true,
  teamName = "",
  teamColor,
  minute,
  score,
}) => {
  const lastTriggerRef = useRef(null);

  const handleEnd = useCallback(() => {
    if (onDone) onDone();
  }, [onDone]);

  useEffect(() => {
    if (!trigger || trigger === lastTriggerRef.current) return undefined;
    lastTriggerRef.current = trigger;
    if (soundEnabled) playGoalSound();
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      handleEnd();
    }, SEQUENCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [trigger, soundEnabled, handleEnd]);

  // Pre-compute confetti once per trigger so render stays pure.
  const confetti = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 52; i += 1) {
      let seed = (typeof trigger === "string" ? trigger.length : Number(trigger) || 0)
        + i * 9301 + 49297;
      const rnd = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
      };
      arr.push({
        idx: i,
        cx: `${Math.round(rnd() * 220 - 110)}vw`,
        cy: `${Math.round(rnd() * 220 - 110)}vh`,
        delay: `${(1.7 + rnd() * 0.6).toFixed(2)}s`,
        w: 8 + Math.round(rnd() * 6),
        h: 12 + Math.round(rnd() * 8),
      });
    }
    return arr;
  }, [trigger]);

  if (!trigger) return null;

  const teamSide = side === "away" ? "away" : "home";
  const bannerColor = teamColor || (teamSide === "away" ? "#dc2626" : "#2563eb");
  const homeScore = score?.home;
  const awayScore = score?.away;
  const showScore = homeScore != null && awayScore != null;

  return (
    <div
      className="fixed inset-0 z-[60] pointer-events-none overflow-hidden"
      data-testid="goal-celebration"
      aria-live="polite"
      role="status"
    >
      <style>{`
        @keyframes gc-flash {
          0% { opacity: 0; }
          6% { opacity: 1; }
          85% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes gc-video-in {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0; filter: blur(10px); }
          18%  { transform: translate(-50%, -50%) scale(1.04); opacity: 1; filter: blur(0); }
          30%  { transform: translate(-50%, -50%) scale(1); }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes gc-banner-in {
          0%   { transform: translateX(-110%); opacity: 0; }
          10%  { opacity: 1; }
          25%  { transform: translateX(0); }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes gc-text-pop {
          0%   { transform: translateY(60px) scale(0.5); opacity: 0; filter: blur(8px); }
          40%  { transform: translateY(0)    scale(1.18); opacity: 1; filter: blur(0); }
          55%  { transform: translateY(0)    scale(0.96); }
          70%  { transform: translateY(0)    scale(1.02); }
          100% { transform: translateY(0)    scale(1); opacity: 1; }
        }
        @keyframes gc-score-in {
          0%   { transform: translateY(20px) scale(0.85); opacity: 0; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes gc-confetti {
          0%   { transform: translate(-50%, -50%) rotate(0deg);  opacity: 1; }
          100% { transform: translate(var(--cx), var(--cy)) rotate(820deg); opacity: 0; }
        }

        .gc-flash-bg       { animation: gc-flash 4.6s ease-out forwards; }
        .gc-video-wrap     { animation: gc-video-in 1.1s cubic-bezier(.22,1,.36,1) forwards; will-change: transform, opacity, filter; }
        .gc-banner         { animation: gc-banner-in 0.65s cubic-bezier(.22,1,.36,1) 0.6s both; }
        .gc-text-pop       { animation: gc-text-pop 1.1s cubic-bezier(.22,1,.36,1) 0.8s both; }
        .gc-score-in       { animation: gc-score-in 0.6s cubic-bezier(.22,1,.36,1) 1.2s both; }
        .gc-confetti-piece { animation: gc-confetti 2.6s cubic-bezier(.22,1,.36,1) 0.4s forwards; }

        @media (prefers-reduced-motion: reduce) {
          .gc-flash-bg, .gc-video-wrap, .gc-banner,
          .gc-text-pop, .gc-score-in, .gc-confetti-piece {
            animation-duration: 0.01ms !important;
            animation-delay: 0ms !important;
          }
        }
      `}</style>

      {/* Radial flash + dark backdrop */}
      <div
        className="absolute inset-0 gc-flash-bg"
        style={{
          background:
            "radial-gradient(circle at center, rgba(34,197,94,0.35), rgba(0,0,0,0.85) 70%)",
        }}
      />

      {/* ----- Iter 243: Sora 2 goal video as sole centrepiece ----- */}
      <div
        className="absolute pointer-events-none gc-video-wrap"
        style={{
          left: "50%",
          top: "50%",
          width: "min(60vw, 520px)",
          height: "min(34vw, 290px)",
          transformOrigin: "center",
          filter: "drop-shadow(0 14px 28px rgba(0,0,0,0.55))",
        }}
        data-testid="goal-video-wrap"
      >
        <video
          src="/videos/goal-celebration.mp4"
          autoPlay
          muted
          playsInline
          loop={false}
          className="w-full h-full select-none rounded-lg overflow-hidden"
          style={{
            objectFit: "cover",
            userSelect: "none",
          }}
          data-testid="goal-video"
          onError={(e) => {
            // Fallback to legacy PNG when video fails (codec / network).
            const img = document.createElement("img");
            img.src = "/images/soccer-ball.png";
            img.alt = "";
            img.draggable = false;
            img.className = "w-full h-full select-none";
            img.style.objectFit = "contain";
            e.target.replaceWith(img);
          }}
        />
      </div>

      {/* ----- AiScore-style slide-in GOAL banner ----- */}
      <div
        className="absolute left-0 gc-banner"
        style={{ bottom: "min(18vh, 200px)", maxWidth: "92vw" }}
      >
        <div
          className="flex items-center gap-3 sm:gap-4 pl-5 pr-7 sm:pl-7 sm:pr-10 py-3.5 sm:py-5 shadow-2xl"
          style={{
            background: bannerColor,
            clipPath: "polygon(0 0, calc(100% - 22px) 0, 100% 100%, 0 100%)",
          }}
          data-testid="goal-banner"
        >
          <span
            className="font-display font-extrabold tracking-[-0.02em] text-white"
            style={{ fontSize: "clamp(28px, 5.5vw, 56px)", lineHeight: 1 }}
          >
            GOAL
          </span>
          {teamName && (
            <span className="text-white/90 font-semibold text-sm sm:text-base hidden sm:inline">
              {teamName}
            </span>
          )}
        </div>
      </div>

      {/* ----- Big GOOOAL takeover text (cinematic) ----- */}
      <div
        className="absolute left-0 right-0 flex flex-col items-center justify-center text-center gc-text-pop"
        style={{ top: "10%" }}
      >
        <div
          className="font-display font-extrabold tracking-[-0.05em] leading-none select-none"
          style={{
            color: "#ffffff",
            fontSize: "clamp(72px, 17vw, 240px)",
            textShadow: "0 12px 60px rgba(0,0,0,0.7), 0 0 80px rgba(34,197,94,0.5)",
            WebkitTextStroke: "3px #0a0a0a",
          }}
          data-testid="goal-text"
        >
          GOOOAL!
        </div>
        {scorer && (
          <div
            className="mt-2 text-white font-display font-semibold drop-shadow-lg"
            style={{ fontSize: "clamp(16px, 2.4vw, 28px)" }}
            data-testid="goal-scorer"
          >
            {scorer}
          </div>
        )}
      </div>

      {/* ----- Score badge (bottom-left, last to appear) ----- */}
      {(showScore || minute != null) && (
        <div
          className="absolute gc-score-in"
          style={{ bottom: "min(8vh, 90px)", left: "min(5vw, 32px)" }}
        >
          <div
            className="flex items-center gap-3 px-3.5 py-2.5 rounded-md shadow-2xl border border-white/10"
            style={{ background: "rgba(20, 20, 20, 0.92)", backdropFilter: "blur(8px)" }}
            data-testid="goal-score-badge"
          >
            {minute != null && (
              <span className="text-white/70 font-mono text-xs sm:text-sm">{minute}&#39;</span>
            )}
            {showScore && (
              <span className="font-display text-white font-bold tabular-nums text-lg sm:text-2xl">
                {homeScore} : {awayScore}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ----- Confetti burst ----- */}
      {confetti.map((c) => {
        const colors = ["#BFEA2B", "#ffffff", "#22c55e", "#f59e0b", bannerColor];
        const bg = colors[c.idx % colors.length];
        return (
          <span
            key={c.idx}
            className="gc-confetti-piece absolute top-1/2 left-1/2 pointer-events-none"
            style={{
              "--cx": c.cx,
              "--cy": c.cy,
              background: bg,
              width: c.w,
              height: c.h,
              borderRadius: 2,
              animationDelay: c.delay,
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
          />
        );
      })}
    </div>
  );
};

export default GoalCelebration;
